import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ChevronLeft, ChevronRight, Loader2, Package,
  MapPin, XCircle, RefreshCw, Truck
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

import { getStatusLabel as getCentralStatusLabel } from '../constants/statuses';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  planifiee: { label: 'Planifié', color: 'bg-slate-500', dotColor: 'bg-slate-400' },
  planned: { label: 'Planifié', color: 'bg-slate-500', dotColor: 'bg-slate-400' },
  demande_enregistree: { label: 'Enregistrée', color: 'bg-slate-400', dotColor: 'bg-slate-300' },
  devis_envoye: { label: 'Devis envoyé', color: 'bg-indigo-500', dotColor: 'bg-indigo-400' },
  devis_accepte: { label: 'Devis accepté', color: 'bg-blue-500', dotColor: 'bg-blue-400' },
  en_cours_de_chargement: { label: 'Chargement', color: 'bg-blue-500', dotColor: 'bg-blue-400' },
  charge_en_route: { label: 'En route', color: 'bg-amber-500', dotColor: 'bg-amber-400' },
  en_transit: { label: 'En transit', color: 'bg-amber-500', dotColor: 'bg-amber-400' },
  en_cours_de_dechargement: { label: 'Déchargement', color: 'bg-purple-500', dotColor: 'bg-purple-400' },
  in_progress: { label: 'En cours', color: 'bg-amber-500', dotColor: 'bg-amber-400' },
  terminee: { label: 'Terminée', color: 'bg-emerald-500', dotColor: 'bg-emerald-400' },
  completed: { label: 'Terminé', color: 'bg-emerald-500', dotColor: 'bg-emerald-400' },
  annulee: { label: 'Annulée', color: 'bg-red-500', dotColor: 'bg-red-400' },
  cancelled: { label: 'Annulé', color: 'bg-red-500', dotColor: 'bg-red-400' }
};

const ClientCalendar = () => {
  const { user } = useAuth();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchContracts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contracts?status=active`);
      setContracts(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      
      let startDate, endDate;
      if (viewMode === 'month') {
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      } else if (viewMode === 'day') {
        startDate = new Date(currentDate);
        endDate = new Date(currentDate);
      } else {
        const dayOfWeek = currentDate.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        startDate = new Date(currentDate);
        startDate.setDate(currentDate.getDate() + mondayOffset);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
      }

      const params = new URLSearchParams({
        limit: '500',
        date_from: startDate.toISOString().split('T')[0],
        date_to: endDate.toISOString().split('T')[0]
      });
      
      if (selectedContract !== 'all') {
        params.append('contract_id', selectedContract);
      }

      // Fetch contract runs + transport requests in parallel
      const [runsRes, opsRes] = await Promise.all([
        axios.get(`${API}/contract-runs?${params}`).catch(() => ({ data: [] })),
        axios.get(`${API}/transport-requests?limit=500`).catch(() => ({ data: [] }))
      ]);

      const runs = runsRes.data?.items || runsRes.data || [];
      const ops = Array.isArray(opsRes.data) ? opsRes.data : [];
      
      const runEvents = runs.map(run => ({
        id: run.id,
        type: 'contract_run',
        title: run.runNumber || `Transfert #${run.id?.slice(0, 6)}`,
        date: run.date,
        status: run.status,
        contractNumber: run.contractNumber,
        contractId: run.contractId,
        departure: run.stops?.[0]?.address?.split(',')[0] || 'Départ',
        arrival: run.stops?.[run.stops?.length - 1]?.address?.split(',')[0] || 'Arrivée',
        vehicle: run.plannedVehiclePlate,
        driver: run.plannedDriverName,
        shift: run.shift
      }));

      // Map operations to calendar events
      const dateFrom = startDate.toISOString().split('T')[0];
      const dateTo = endDate.toISOString().split('T')[0];
      const opEvents = ops
        .filter(op => {
          const opDate = (op.deliveryDate || op.createdAt || '').slice(0, 10);
          return opDate >= dateFrom && opDate <= dateTo;
        })
        .map(op => ({
          id: op.id,
          type: 'operation',
          title: op.operationNumber || `Op #${op.id?.slice(0, 6)}`,
          date: (op.deliveryDate || op.createdAt || '').slice(0, 10),
          status: op.status === 'terminee' ? 'completed' : op.status === 'annulee' ? 'cancelled' : op.status || 'planned',
          departure: op.departure?.split(',')[0] || '',
          arrival: op.arrival?.split(',')[0] || '',
          vehicle: op.vehicleType || '',
          driver: '',
          shift: ''
        }));

      setEvents([...runEvents, ...opEvents]);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Erreur lors du chargement des opérations');
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode, selectedContract]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    } else if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() - 1);
      setCurrentDate(newDate);
    } else {
      newDate.setDate(currentDate.getDate() - 7);
      setCurrentDate(newDate);
    }
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    } else if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() + 1);
      setCurrentDate(newDate);
    } else {
      newDate.setDate(currentDate.getDate() + 7);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => setCurrentDate(new Date());

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    for (let i = startPadding - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const getWeekDays = (date) => {
    const dayOfWeek = date.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(date);
    monday.setDate(date.getDate() + mondayOffset);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      days.push({ date: d, isCurrentMonth: true });
    }
    return days;
  };

  const getEventsForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toISOString().split('T')[0] === dateStr;
    });
  };

  const formatMonthYear = (date) => date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  
  const isToday = (date) => date.toDateString() === new Date().toDateString();

  const days = viewMode === 'month' ? getDaysInMonth(currentDate) : getWeekDays(currentDate);
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="space-y-6" data-testid="client-calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-900">Calendrier des opérations</h1>
          <p className="text-sm text-gray-500">Vue d'ensemble de toutes vos opérations planifiées</p>
        </div>
        <Button variant="outline" onClick={fetchEvents} disabled={loading}>
          <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
          Actualiser
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={goToPrevious}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={goToToday} className="text-sm">
                Aujourd'hui
              </Button>
              <Button variant="outline" size="icon" onClick={goToNext}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="text-lg font-medium ml-4">{formatMonthYear(currentDate)}</span>
            </div>

            <div className="flex items-center gap-4">
              <Select value={selectedContract} onValueChange={setSelectedContract}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Tous les contrats" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les contrats</SelectItem>
                  {contracts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.contractNumber}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-[120px]" data-testid="calendar-view-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Jour</SelectItem>
                  <SelectItem value="week">Semaine</SelectItem>
                  <SelectItem value="month">Mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center h-[500px]">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : viewMode === 'day' ? (
            <div data-testid="calendar-day-view">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-500">{weekDays[(currentDate.getDay() + 6) % 7]}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {currentDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
                </p>
              </div>
              {(() => {
                const dayEvents = getEventsForDay(currentDate);
                if (dayEvents.length === 0) {
                  return (
                    <div className="text-center py-12 text-slate-400 text-sm">
                      Aucun événement ce jour.
                    </div>
                  );
                }
                return (
                  <div className="space-y-2 max-w-2xl mx-auto">
                    {dayEvents.map((event) => {
                      const statusConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.planned;
                      return (
                        <div
                          key={event.id}
                          onClick={() => setSelectedEvent(event)}
                          className={cn(
                            "p-3 rounded-lg cursor-pointer hover:shadow-md transition border-l-4",
                            statusConfig.color, "text-white",
                          )}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="font-semibold">{event.title}</p>
                              <p className="text-xs opacity-90 mt-0.5">
                                {event.departure} → {event.arrival}
                              </p>
                            </div>
                            {event.shift === 'night' && <span className="text-xs">🌙 Nuit</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map((day) => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">{day}</div>
                ))}
              </div>

              <div className={cn("grid grid-cols-7 gap-1", viewMode === 'week' ? 'h-[400px]' : '')}>
                {days.map(({ date, isCurrentMonth }, idx) => {
                  const dayEvents = getEventsForDay(date);
                  const isCurrentDay = isToday(date);

                  return (
                    <div
                      key={idx}
                      className={cn(
                        "border rounded-lg p-1 transition-colors",
                        viewMode === 'month' ? 'min-h-[100px]' : 'h-full',
                        isCurrentMonth ? 'bg-white' : 'bg-gray-50',
                        isCurrentDay && 'border-red-500 border-2'
                      )}
                    >
                      <div className={cn(
                        "text-sm font-medium mb-1 px-1",
                        isCurrentMonth ? 'text-gray-900' : 'text-gray-400',
                        isCurrentDay && 'text-red-600'
                      )}>
                        {date.getDate()}
                      </div>

                      <div className="space-y-1 overflow-y-auto" style={{ maxHeight: viewMode === 'month' ? '70px' : '350px' }}>
                        {dayEvents.slice(0, viewMode === 'month' ? 3 : 20).map((event) => {
                          const statusConfig = STATUS_CONFIG[event.status] || STATUS_CONFIG.planned;
                          return (
                            <div
                              key={event.id}
                              className={cn(
                                "text-xs p-1 rounded cursor-pointer truncate hover:opacity-80 transition-opacity",
                                statusConfig.color, "text-white"
                              )}
                              onClick={() => setSelectedEvent(event)}
                              title={`${event.title} - ${event.departure} -> ${event.arrival}`}
                            >
                              {event.shift === 'night' && '* '}{event.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > (viewMode === 'month' ? 3 : 20) && (
                          <div className="text-xs text-gray-500 px-1">
                            +{dayEvents.length - (viewMode === 'month' ? 3 : 20)} autres
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            {Object.entries(STATUS_CONFIG).filter(([key]) => 
              ['planned', 'en_cours_de_chargement', 'en_transit', 'completed', 'cancelled'].includes(key)
            ).map(([status, config]) => (
              <div key={status} className="flex items-center gap-2">
                <div className={cn("w-3 h-3 rounded-full", config.dotColor)} />
                <span className="text-sm text-gray-600">{config.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  {selectedEvent.title}
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {new Date(selectedEvent.date).toLocaleDateString('fr-FR', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedEvent(null)}>
                <XCircle className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={cn("px-2 py-1", STATUS_CONFIG[selectedEvent.status]?.color || 'bg-gray-500', "text-white")}>
                  {STATUS_CONFIG[selectedEvent.status]?.label || getCentralStatusLabel(selectedEvent.status)}
                </Badge>
                {selectedEvent.shift === 'night' && <Badge variant="outline">Nuit</Badge>}
              </div>

              <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Départ</p>
                    <p className="text-sm font-medium">{selectedEvent.departure}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Arrivée</p>
                    <p className="text-sm font-medium">{selectedEvent.arrival}</p>
                  </div>
                </div>
              </div>

              {(selectedEvent.vehicle || selectedEvent.driver) && (
                <div className="space-y-2">
                  {selectedEvent.vehicle && (
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-gray-400" />
                      <span>{selectedEvent.vehicle}</span>
                    </div>
                  )}
                  {selectedEvent.driver && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="w-4 h-4 text-gray-400">Chauffeur:</span>
                      <span>{selectedEvent.driver}</span>
                    </div>
                  )}
                </div>
              )}

              {selectedEvent.contractNumber && (
                <div className="text-sm text-gray-500">Contrat: {selectedEvent.contractNumber}</div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClientCalendar;
